import { CONNECTIONS, type Connection, LOG_GROUPS, LOG_LEVEL, getConnectionState, storage } from '@b2x/shared';
import type { BusinessContext } from '../types';
import { BusinessType } from '../types/enums';
import type { PersistedState, Profile, ProfileState } from '../types/profile-type';
import { B2X_CUSTOMER_BROADCAST_CHANNEL } from '../utilities/constants';
import { logger } from '../utilities/logger';

export async function persistProfileState(key: string, state: ProfileState): Promise<void> {
  const { jwe = null } = (await getConnectionState(CONNECTIONS.AUTH_CONNECTION_NAME as Connection)) as {
    jwe: string | null;
  };
  const { profileId = null } = storage.local.get<PersistedState>(key, {
    profileId: null,
    businessType: BusinessType.B2C,
  });
  const currentProfileState = storage.session.get<ProfileState>(key);

  const persistedState = <PersistedState>{
    businessType: state.activeProfile.businessType,
    profileId: jwe && state.activeProfile.businessType === BusinessType.B2B ? state.activeProfile.id : profileId,
  };

  const cleanState = Object.assign<object, ProfileState>(
    Object.create(null),
    JSON.parse(JSON.stringify(persistedState)),
  );

  storage.local.set(key, cleanState);
  storage.session.set(key, state);

  // sync tabs
  if (JSON.stringify(state) !== JSON.stringify(currentProfileState)) {
    logger('Sending new state to other tabs', {
      args: [],
      group: LOG_GROUPS.BOOTSTRAP,
      level: LOG_LEVEL.INFO,
    });

    const channel = new BroadcastChannel(B2X_CUSTOMER_BROADCAST_CHANNEL);
    channel.postMessage({ key, value: state });
  }
}

export function defineActiveProfile(condition: BusinessContext<BusinessType>, profiles: Profile[]): Profile {
  const { businessType, profileId } = condition;

  const firstProfile = profiles.at(0) as Profile;

  // "error" profile
  if (!firstProfile.id) {
    return firstProfile;
  }

  // B2C as active profile
  if (businessType === BusinessType.B2C) {
    return profiles.find((profile) => profile.businessType === BusinessType.B2C) ?? firstProfile;
  }

  // B2B as active profile
  return resolveActiveB2bProfile(profiles, profileId) ?? firstProfile;
}

function resolveActiveB2bProfile(profiles: Profile[], profileId: string | null): Profile | undefined {
  if (profileId) {
    const foundProfileById = profiles.find((profile) => profile.id === profileId);

    if (foundProfileById) {
      return foundProfileById;
    }

    const foundProfileByMeId = profiles.find((profile) => profile.meId === profileId);

    if (foundProfileByMeId) {
      return foundProfileByMeId;
    }
  }

  return profiles.find((profile) => profile.businessType === BusinessType.B2B);
}
